import {
  FindPlaceCandidate,
  PlaceAutocompletePrediction,
} from "../apis/google";
import { useEffect, useRef, useState, useTransition } from "react";
import InputOptions, { InputOptionsOption } from "./InputOptions";
import TextField from "./TextField";
import { APIs } from "../apis";
import { SVGs } from "../assets";
import { useAuthStore } from "../stores";

export type GooglePlacesSearchProps = {
  label: string;
  placeholder: string;
  onSelect: (place: FindPlaceCandidate) => void;
  className?: string;
  value?: string;
};

export default function GooglePlacesSearch({
  label,
  placeholder,
  value,
  onSelect,
  className,
}: GooglePlacesSearchProps) {
  const [predictions, setPredictions] = useState<PlaceAutocompletePrediction[]>(
    [],
  );
  const [isPending, startTransition] = useTransition();
  const inputRef = useRef<HTMLInputElement>(null);
  const location = useAuthStore((s) => s.user?.location?.location);

  useEffect(() => {
    if (inputRef.current && value) {
      inputRef.current.value = value;
    }
  }, [value]);

  function onPredictionSelect(_: string, option: InputOptionsOption) {
    startTransition(async () => {
      try {
        onSelect(await APIs.google.getPlace(option.value));
      } catch {
        /* keep current selection */
      }
    });
  }

  function onQueryChange({ target }: React.ChangeEvent<HTMLInputElement>) {
    const query = target.value.trim();
    if (!query.length) {
      setPredictions([]);
      return;
    }
    startTransition(async () => {
      setPredictions(
        await APIs.google.autocompletePlaces(query, {
          ...(location && {
            locationBias: {
              circle: { center: location, radius: 50000 },
            },
          }),
        }),
      );
    });
  }

  return (
    <InputOptions
      classNames={{ menu: "min-w-[250px]" }}
      onChange={onPredictionSelect}
      options={predictions.map((prediction) => {
        return {
          value: prediction.placeId,
          label:
            prediction.structuredFormat?.mainText?.text ??
            prediction.text?.text ??
            "",
          subtitle: prediction.structuredFormat?.secondaryText?.text,
          icon: (
            <SVGs.LocationMarker className="mt-[3px] h-[18px] w-[18px] shrink-0 text-text-primary" />
          ),
        };
      })}
    >
      <TextField
        ref={inputRef}
        variant="surface"
        label={label}
        className={className}
        placeholder={placeholder}
        onChange={onQueryChange}
        endAddon={
          isPending ? (
            <div className="ml-2 flex h-4 w-4 items-center justify-center">
              <div
                aria-label="Loading"
                className="h-4 w-4 animate-spin rounded-full border-2 border-text-primary/20 border-t-text-primary/70"
              />
            </div>
          ) : null
        }
      />
    </InputOptions>
  );
}
